import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { ArrowRight, Users as UsersIcon, CalendarDays, MapPin } from "lucide-react";
import { SPORT_META } from "@/context/SportContext";

const ACCENT = "#FF4500";

export default function PublicTeam() {
  const { id } = useParams();
  const [team, setTeam] = useState(null);

  useEffect(() => {
    api.get(`/public/teams/${id}`).then((r) => setTeam(r.data)).catch(() => setTeam({ notFound: true }));
  }, [id]);

  if (!team) return <div className="min-h-screen flex items-center justify-center text-[#A3A3A3]"><div className="label-mono">Loading…</div></div>;
  if (team.notFound) return <div className="min-h-screen flex items-center justify-center text-[#A3A3A3]">Team not found.</div>;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white noise-bg">
      <header className="px-8 py-6 max-w-7xl mx-auto flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center" style={{ background: ACCENT }}><span className="text-black font-black text-lg">R</span></div>
          <span className="h-display text-2xl tracking-tighter">RALLY LAB</span>
        </Link>
        <Link to="/register" className="px-5 py-2.5 text-xs font-bold uppercase tracking-wider" style={{ background: ACCENT, color: "#0A0A0A" }}>Join as a player</Link>
      </header>

      <section className="max-w-5xl mx-auto px-8 py-12">
        <div className="label-mono mb-3 flex items-center gap-2" style={{ color: ACCENT }}><UsersIcon className="w-3.5 h-3.5" /> COACH TEAM</div>
        <h1 className="h-display text-5xl md:text-6xl tracking-tighter mb-3">{team.name}</h1>
        {team.tagline && <p className="text-xl text-[#E5E5E5] mb-4">{team.tagline}</p>}
        {team.description && <p className="text-[#A3A3A3] leading-relaxed mb-10 max-w-2xl">{team.description}</p>}

        <div className="mb-12">
          <div className="label-mono mb-4">COACHES · {team.members?.length || 0}</div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#262626]">
            {(team.members || []).map((c) => (
              <Link key={c.id} to={`/coaches/${c.id}`} className="bg-[#0A0A0A] p-5 hover:bg-[#141414] group">
                <div className="flex items-start justify-between mb-2">
                  <div className="w-10 h-10 flex items-center justify-center font-bold" style={{ background: ACCENT + "20", color: ACCENT }}>{c.name?.[0]}</div>
                  {c.years_experience > 0 && <div className="text-right text-xs"><div className="label-mono text-[0.55rem]">YEARS</div><div className="h-display text-lg" style={{ color: ACCENT }}>{c.years_experience}</div></div>}
                </div>
                <div className="font-semibold">{c.name}</div>
                {c.headline && <div className="text-xs text-[#A3A3A3] mt-1 line-clamp-2">{c.headline}</div>}
                <div className="text-xs uppercase tracking-wider mt-3 group-hover:gap-2 gap-1 inline-flex items-center transition-all" style={{ color: ACCENT }}>View <ArrowRight className="w-3 h-3" /></div>
              </Link>
            ))}
          </div>
        </div>

        <div>
          <div className="label-mono mb-4 flex items-center gap-2"><CalendarDays className="w-3.5 h-3.5 text-[#CCFF00]" /> UPCOMING SESSIONS</div>
          {!(team.offerings || []).length ? (
            <div className="text-sm text-[#A3A3A3]">No upcoming sessions published by this team.</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#262626]">
              {team.offerings.map((o) => {
                const d = new Date(o.start);
                const m = SPORT_META[o.sport] || { color: ACCENT };
                return (
                  <div key={o.id} className="bg-[#0A0A0A] p-5">
                    <div className="flex justify-between mb-2">
                      <div className="label-mono" style={{ color: m.color }}>{o.sport}</div>
                      <div className="h-display text-xl" style={{ color: m.color }}>£{o.price_per_player}</div>
                    </div>
                    <div className="font-semibold text-sm mb-1">{o.title}</div>
                    <div className="text-xs text-[#A3A3A3] mb-0.5">{d.toLocaleString(undefined, { weekday:"short", day:"numeric", month:"short", hour:"2-digit", minute:"2-digit"})}</div>
                    <div className="text-xs text-[#A3A3A3] flex items-center gap-1"><MapPin className="w-3 h-3" /> {o.venue}</div>
                    <div className="text-xs text-[#A3A3A3] mt-1"><UsersIcon className="w-3 h-3 inline" /> {o.spaces_left} spaces</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
